import type { BalanceLineComputed } from '../../../types/financial-model-v2';
/**
 * Balance Sheet tab for v2. Entirely computed.
 *
 * Cash at Bank comes from the CASHFLOW, not from a plug. The template derived
 * cash so the sheet would balance, and its plug omitted four lines
 * (Investments, Deferred Tax, Stocks, VAT Receivable) — so it balanced only
 * while all four were zero, which is exactly the state the template shipped in.
 *
 * The Balance Check here is therefore a real test rather than an identity that
 * can only return zero. When it is non-zero the model has an error, which is
 * why it is shown as its own row AND surfaced in the banner on every sheet.
 *
 * `Cash Variance` is a diagnostic: what cash WOULD have to be for the sheet to
 * balance, less what the cashflow actually produced. Non-zero localises an
 * imbalance to a period. See spec 7.4.
 */

import { ComputedDataRow } from '../table/DataRow';
import { GrandTotalRow } from '../table/GrandTotalRow';
import { SectionHeader } from '../table/SectionHeader';
import { SubtotalRow } from '../table/SubtotalRow';
import { useCallback } from 'react';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import { V2EditableRow } from './V2EditableRow';
import { V2Sheet } from './V2Table';


/**
 * The declared balance sheet lines that belong in one section.
 *
 * These carry the captions Mike's template has no row for - provisions,
 * contract liabilities, borrowings, leases - and anything a user added. They
 * are already inside the subtotals; showing them is what lets a reader see
 * WHAT the subtotal is made of, which was the whole complaint about net
 * assets coming out right while the composition stayed hidden.
 */
function DeclaredRows({
  lines,
  side,
  band,
}: {
  lines: Record<string, BalanceLineComputed> | undefined;
  side: string;
  band?: string;
}) {
  const rows = Object.entries(lines ?? {}).filter(
    ([, line]) =>
      line.side === side &&
      (band === undefined || line.band === band) &&
      Object.values(line.closing ?? {}).some((v) => v),
  );
  const updateInput = useFinancialModelV2Store((s) => s.updateInput);
  if (rows.length === 0) return null;
  return (
    <>
      {/* An entered closing balance pins the line; its movement then follows from it. */}
      {rows.map(([key, line]) => (
        <V2EditableRow
          key={key}
          label={line.caption}
          data={line.closing}
          onCellChange={(period, value) => updateInput(`balanceLines.${key}.closingActual`, period, value)}
        />
      ))}
    </>
  );
}

export function BalanceSheetViewV2() {
  const model = useFinancialModelV2Store((s) => s.model);
  const updateInput = useFinancialModelV2Store((s) => s.updateInput);
  const handleChange = useCallback(
    (path: string) => (period: string, value: number) => updateInput(path, period, value),
    [updateInput],
  );
  if (!model) return null;
  const bs = model.computed.balanceSheet;

  const balances = Object.values(bs.balanceCheck).every((v) => Math.abs(v) < 1);

  return (
    <V2Sheet>
      <SectionHeader label="Fixed Assets" />
      <ComputedDataRow label="Fixed Assets (Gross)" data={bs.fixedAssetsGross} />
      <ComputedDataRow label="Accumulated Depreciation" data={bs.accumulatedDepreciation} />
      {/* IAS 1 asks for these separately on the face. The schedule always
          had the split; goodwill and a fleet of vans should not read as one
          number. */}
      <ComputedDataRow
        label="— Property, plant and equipment"
        data={bs.propertyPlantEquipment ?? {}}
      />
      <ComputedDataRow
        label="— Intangible assets"
        data={bs.intangibleAssets ?? {}}
      />
      <SubtotalRow label="Net Fixed Assets" data={bs.netFixedAssets} />

      <SectionHeader label="Other Non-Current Assets" />
      <ComputedDataRow label="Investments" data={bs.investments} />
      <ComputedDataRow label="Deferred Tax Asset" data={bs.deferredTaxAsset} />
      <DeclaredRows lines={bs.balanceLines} side="asset" band="non_current" />

      <SectionHeader label="Current Assets" />
      <ComputedDataRow label="Stocks" data={bs.stocks} />
      <ComputedDataRow label="Trade Debtors" data={bs.tradeDebtors} />
      <ComputedDataRow label="Other Debtors" data={bs.otherDebtors} />
      <ComputedDataRow label="VAT Receivable" data={bs.vatReceivable} />
      <ComputedDataRow label="Cash at Bank (from Cashflow)" data={bs.cashAtBank} />
      {/* IAS 7 paragraph 45. The total above is what the cash flow produced;
          these say where the money is, which is the difference between a current
          account and a fund that takes three days to sell. Synthasia holds
          163,054,168 in money market funds against 56,824,642 at bank. */}
      <ComputedDataRow label="— cash at bank" data={bs.cashInBank ?? {}} />
      {/* Entered: the part of cash held as equivalents (IAS 7 paragraph 45). */}
      <V2EditableRow
        label="— cash equivalents"
        data={model.inputs.controlAccount.cashEquivalentsActual ?? {}}
        onCellChange={handleChange('controlAccount.cashEquivalentsActual')}
      />
      <DeclaredRows lines={bs.balanceLines} side="asset" band="current" />
      <SubtotalRow label="Total Current Assets" data={bs.totalCurrentAssets} />
      <GrandTotalRow label="Total Assets" data={bs.totalAssets} />

      <SectionHeader label="Liabilities" />
      <ComputedDataRow label="Trade Creditors" data={bs.tradeCreditors} />
      <ComputedDataRow
        label="Corporation Tax"
        hint={{ how: 'The corporation tax owed at the year end: the closing balance of the tax section on the Control Account.', change: 'The tax charge on the P&L; the share owed at the year end on Settings; or, in an actual year, the closing balance on the Control Account.' }}
        data={bs.corporationTax}
      />
      <ComputedDataRow label="Other Creditors" data={bs.otherCreditors} />
      <DeclaredRows lines={bs.balanceLines} side="liability" band="current" />
      <SubtotalRow label="Total Current Liabilities" data={bs.totalCurrentLiabilities} />
      {/* Non-current, as IAS 12 paragraph 70 requires: never a current
          liability however soon it may reverse. Inside the row above, and
          shown separately because a reader cannot otherwise tell how much of
          the long-term creditors is tax that has not fallen due. */}
      {Object.values(bs.deferredTaxLiability ?? {}).some((v) => v) && (
        <ComputedDataRow
          label="— of which deferred tax"
          data={bs.deferredTaxLiability ?? {}}
        />
      )}
      <ComputedDataRow label="Creditors over one year" data={bs.creditorsOverOneYear} />
      <DeclaredRows lines={bs.balanceLines} side="liability" band="non_current" />
      <SubtotalRow label="Total Liabilities" data={bs.totalLiabilities} />
      <GrandTotalRow
        label="Total Assets less Liabilities"
        data={bs.totalAssetsLessLiabilities}
      />

      <SectionHeader label="Equity" />
      <ComputedDataRow label="Share Capital" data={bs.shareCapital} />
      <ComputedDataRow label="Share-based payment reserve" data={bs.shareBasedPaymentReserve} />
      <ComputedDataRow label="Retained Earnings" data={bs.retainedEarnings} />
      {/* Accumulated other comprehensive income. Inside total equity, and on
          its own row so a reader can see which part of equity can still reach
          earnings — a translation reserve is recycled on disposal, a property
          revaluation never is. */}
      <ComputedDataRow
        label="Other comprehensive income reserve"
        data={bs.ociReserve ?? {}}
      />
      <GrandTotalRow label="Total Equity" data={bs.totalEquity} />

      <SectionHeader
        label={balances ? 'Checks — balanced' : 'Checks — DOES NOT BALANCE'}
      />
      <GrandTotalRow label="Balance Check (must be zero)" data={bs.balanceCheck} />
      <ComputedDataRow label="Cash Variance (diagnostic)" data={bs.cashVariance} />

      {/* The Companies House proof, as Baseline Accounting and the template keep it (r43). */}
      <SectionHeader label="Companies House proof" />
      <V2EditableRow
        label="Filed net assets (Companies House)"
        data={model.inputs.filedNetAssets ?? {}}
        onCellChange={handleChange('filedNetAssets')}
      />
      <ComputedDataRow label="Companies House proof (filed less model)" data={bs.chProofNetAssets ?? {}} />
    </V2Sheet>
  );
}
